import * as React from 'react';
import { useSelector } from 'react-redux';

import { getTikTokThumbnailUrlFromVideoUrl } from 'utils/tiktok-thumbnail';
import { fetchInstagramThumbnailUrl } from 'utils/content/instagram-gql-fallback';

import type { State } from 'types';
import type { ProposalImage } from 'modules/proposal/types/Proposal.type';
import type { ProposalTileChannelType } from 'modules/proposal/types/ProposalTile.type';

export function useProposalImagePrefetch(
  posts: Array<ProposalImage | null>,
  channelType: ProposalTileChannelType,
  token: string
): Array<ProposalImage | null> {
  // fetch helpers
  const [preloadedPosts, setPreloadedPosts] = React.useState<Array<ProposalImage | null>>(posts);
  const userConsents = useSelector((state: State) => state.consentReducer.consents);

  const fetchKey = posts.map(post => (
    `${post?.url ?? '-'}?loading=${!!post?.isLoading}`
  )).join('');

  React.useEffect(() => {
    if (!userConsents[channelType]) {
      setPreloadedPosts(posts);
      return;
    }

    const promises = posts.map((post) => {
      if (!post || post.assetUrl || !post.url) {
        // skip filled posts
        return Promise.resolve(post);
      }

      if (channelType === 'instagram') {
        return fetchInstagramThumbnailUrl(post.url ?? '', token)
          .then((assetUrl) => ({ ...post, isNotAvailable: !assetUrl, assetUrl }))
          .catch(() => ({ isNotAvailable: true, ...post }));
      }

      if (channelType === 'tiktok') {
        return getTikTokThumbnailUrlFromVideoUrl(post.url ?? '', token)
          .then((assetUrl) => ({ ...post, isNotAvailable: !assetUrl, assetUrl }))
          .catch(() => ({ isNotAvailable: true, ...post }));
      }

      return Promise.resolve(post);
    });

    Promise.all(promises).then(result => setPreloadedPosts(result));
  }, [fetchKey]);

  return preloadedPosts;
}
